'use client'; // Mark as a Client Component

import { useCalendlyPopup } from '@/hooks/useCalendlyPopup';
import { Link } from '@/i18n/routing';
import { ADVOCATE_LANDING_ROUTE } from '@/lib/compliance/advocate-landing';
import {
  PLATFORM_NEUTRALITY_POLICY_PUBLISHED,
  PLATFORM_NEUTRALITY_ROUTE,
} from '@/lib/compliance/platform-neutrality';
import Logo from '@/lib/logo/Logo';
import { SOCIAL_MEDIA_LINKS } from '@/lib/social-links';
import { cn } from '@/lib/utils';
import { LLMS_TXT_PATH } from '@/markdowns/doc/_paths';
import { CalendarDays } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { usePostHog } from 'posthog-js/react';

interface FooterProps {
  /**
   * Render the advocate landing page (#7230) link — launch checklist item 3
   * (#7951). Driven by the PUBLISHED gate, not mere reachability: the page is
   * already live in production but deliberately unlisted, so linking it is the
   * separate launch decision. Passed from the marketing layout (a server
   * component) because the env is server-only — unlike the platform-neutrality
   * gate below (a compile-time const) it cannot be imported here.
   */
  advocateLandingPublished?: boolean;
  /**
   * The `<CityHubLinks/>` element, rendered by the marketing layout and passed
   * down as a slot. It CANNOT be imported here: it is an async SERVER component
   * and this file is `'use client'`. Passing the already-rendered element keeps
   * its 47 anchors in the initial HTML — which is the entire point of the
   * component, since a JS-gated list would not fix the crawl problem it exists
   * for. Optional so the footer degrades to its previous shape if a future
   * layout mounts it without the slot.
   */
  cityHubLinks?: React.ReactNode;
}

export default function Footer({
  advocateLandingPublished = false,
  cityHubLinks,
}: FooterProps) {
  const date = new Date();
  const t = useTranslations('Footer');
  const posthog = usePostHog();
  const { openCalendly, isLoading: isCalendlyLoading } = useCalendlyPopup();
  const shortDescription = t('shortDescription');

  return (
    <footer
      className={cn(
        'bg-gradient-to-r from-brand-tertiary/20 to-brand-secondary/20 text-xs sm:text-sm lg:text-[14px] text-content-primary',
        'bottom-0 min-h-[6vh] w-full mt-auto footer-sticky'
      )}
    >
      <div className='container mx-auto px-3 sm:px-4 py-2 sm:py-3 pb-0 sm:pb-0'>
        <div className='flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3 sm:gap-2 pb-2'>
          <div className='flex flex-col items-center sm:items-start gap-1'>
            <div className='flex flex-row gap-3 sm:gap-4 items-center'>
              <div className='flex items-center shrink-0'>
                <Logo isFooter size='sm' />
              </div>
              {/* Brand tagline — NOT the page's main heading. Using <h1> here
                  produced 2 H1s on every page (footer + hero), which failed
                  the "exactly one descriptive H1" SEO rule across all 16
                  canonical URLs (Wave 7 Tier 2 CI finding). */}
              <p className='text-[11px] sm:text-xs tracking-wide font-sans font-normal text-center sm:text-left'>
                {shortDescription}
              </p>
            </div>
          </div>
          <div className='flex flex-row gap-3 sm:gap-4 items-center sm:items-end pt-2'>
            <button
              type='button'
              className='flex items-center gap-1.5 text-brand-tertiary/70 hover:text-brand-tertiary transition-colors duration-200 text-xs sm:text-sm font-medium disabled:cursor-wait disabled:opacity-60'
              aria-label={t('scheduleCall')}
              aria-busy={isCalendlyLoading}
              disabled={isCalendlyLoading}
              onClick={() => {
                posthog?.capture('calendly_link_clicked', { source: 'footer' });
                openCalendly();
              }}
            >
              <CalendarDays size={16} />
            </button>
            {SOCIAL_MEDIA_LINKS.map((socialLink) => {
              const IconComponent = socialLink.icon;
              return (
                <a
                  key={socialLink.name}
                  href={socialLink.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-content-primary hover:text-content-secondary transition-colors duration-200'
                  aria-label={socialLink.ariaLabel}
                >
                  {socialLink.useDangerouslySetInnerHTML &&
                  socialLink.htmlContent ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: socialLink.htmlContent,
                      }}
                      className='hover:scale-110 transition-transform duration-200'
                    />
                  ) : IconComponent ? (
                    <IconComponent
                      size={20}
                      className='hover:scale-110 transition-transform duration-200'
                    />
                  ) : null}
                </a>
              );
            })}
          </div>
        </div>
        {/* City hub links sit in their own labelled block ABOVE the divider,
            the standard footer shape for a location index — the copyright and
            legal row stays last, where people look for it. */}
        {cityHubLinks}
        <hr className='border-line-default my-1.5' />
        <div className='flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 py-1'>
          <p className='text-xs sm:text-sm'>
            © {date.getUTCFullYear()} Lexomat
          </p>
          <Link
            href='/doc'
            className='text-xs sm:text-sm text-brand-tertiary/70 hover:text-brand-tertiary transition-colors duration-200'
          >
            {t('docs')}
          </Link>
          {/* llms.txt — the LLM-facing index of the docs (llmstxt.org). A plain
              anchor, not the locale-aware Link: the file lives at the site
              root outside the locale tree (see markdowns/doc/_llms.ts). */}
          <a
            href={LLMS_TXT_PATH}
            className='text-xs sm:text-sm text-brand-tertiary/70 hover:text-brand-tertiary transition-colors duration-200'
          >
            {t('llmsTxt')}
          </a>
          {/* Advocate landing page link — gated on the same build-time flag
              that opens the route, so the footer never links a 404 (#7951
              launch checklist item 3). */}
          {advocateLandingPublished && (
            <Link
              href={ADVOCATE_LANDING_ROUTE}
              className='text-xs sm:text-sm text-brand-tertiary/70 hover:text-brand-tertiary transition-colors duration-200'
            >
              {t('forLawyers')}
            </Link>
          )}
          {/* Platform-neutrality policy link — gated: rendered only once the
              policy is signed off and published (PLATFORM_NEUTRALITY_POLICY_PUBLISHED,
              a compliance decision, not engineering). Inert until then (#5853). */}
          {PLATFORM_NEUTRALITY_POLICY_PUBLISHED && (
            <Link
              href={PLATFORM_NEUTRALITY_ROUTE}
              className='text-xs sm:text-sm text-brand-tertiary/70 hover:text-brand-tertiary transition-colors duration-200'
            >
              {t('platformNeutrality')}
            </Link>
          )}
        </div>
      </div>
    </footer>
  );
}
